import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const testimonials = [
  {
    name: 'Tina T.',
    role: 'Logistics Manager',
    industry: 'Agriculture & Logistics',
    date: 'Nov. 15, 2024',
    rating: 5,
    review:
      'Clutch Logistics has transformed how we manage transport and fleet operations. The integration with the Clutch Driver App ensures seamless communication between our team and drivers, allowing us to track progress in real-time.',
    logo: '/images/mahonys_logo.jpg',
    product: 'Clutch Logistics',
  },
  {
    name: 'Alec S.',
    role: 'General Manager Import & Export',
    industry: 'Logistics & Supply Chain',
    date: 'Nov. 20, 2024',
    rating: 5,
    review:
      'The Clutch Booking system has been a game-changer for managing our site scheduling. Creating custom booking slots and tracking availability in real-time has improved coordination and reduced site congestion significantly.',
    logo: '/images/mahonys_logo.jpg',
    product: 'Clutch Bookings',
  },
  {
    name: 'Luke M.',
    role: 'Director',
    industry: 'International Trade & Logistics',
    date: 'Dec. 10, 2024',
    rating: 5,
    review:
      'The Clutch Driver App has simplified driver operations like never before. Real-time job assignments, GPS route tracking, and easy reporting — our drivers are more efficient and communication with dispatch has greatly improved.',
    logo: '/images/grainpac_logo.png',
    product: 'Clutch Driver App',
  },
];

function StarRating({ count = 5 }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <svg key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function TestimonialCard({ testimonial, active = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`relative rounded-2xl border p-6 flex flex-col h-full transition-all duration-300 ${
        active
          ? 'border-accent/30 bg-white shadow-card-hover'
          : 'border-border-subtle bg-white hover:border-border-medium hover:shadow-card'
      }`}
    >
      {/* Quote mark */}
      <div className="absolute top-5 right-6 text-5xl text-accent/20 font-serif leading-none select-none">"</div>

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <img
          src={testimonial.logo}
          alt={testimonial.industry}
          className="h-7 w-auto object-contain opacity-80"
        />
        <span className="text-xs text-secondary">{testimonial.date}</span>
      </div>

      <StarRating count={testimonial.rating} />

      {/* Review */}
      <p className="text-secondary text-sm leading-relaxed my-4 flex-1">
        "{testimonial.review}"
      </p>

      {/* Footer */}
      <div className="pt-4 border-t border-border-subtle flex items-end justify-between">
        <div>
          <p className="text-primary text-sm font-semibold">{testimonial.name}</p>
          <p className="text-secondary text-xs mt-0.5">{testimonial.role}</p>
          <p className="text-secondary text-xs">{testimonial.industry}</p>
        </div>
        <span className="badge bg-accent/10 text-accent text-[10px]">{testimonial.product}</span>
      </div>
    </motion.div>
  );
}

const Testimonials = () => {
  const [current, setCurrent] = useState(0);

  return (
    <section className="section-padding bg-surface-1 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-medium to-transparent" />
      <div className="absolute inset-0 bg-mesh-grid pointer-events-none" />

      <div className="section-container relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="section-label">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Client Stories
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            Trusted by Industry<br />
            <span className="text-gradient-blue">Leaders Across Australia</span>
          </h2>
          <p className="text-secondary text-lg max-w-xl mx-auto">
            Discover how businesses are transforming their logistics with Clutch.
          </p>
        </motion.div>

        {/* Desktop: 3-column grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <TestimonialCard key={i} testimonial={t} active={i === 1} />
          ))}
        </div>

        {/* Mobile: carousel */}
        <div className="md:hidden relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.3 }}
            >
              <TestimonialCard testimonial={testimonials[current]} active />
            </motion.div>
          </AnimatePresence>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => setCurrent((p) => (p - 1 + testimonials.length) % testimonials.length)}
              className="w-9 h-9 rounded-full bg-white border border-border-subtle shadow-card flex items-center justify-center hover:bg-surface-2 transition-colors cursor-pointer"
              aria-label="Previous"
            >
              <svg className="w-4 h-4 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex gap-1.5">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                    i === current ? 'bg-accent w-5 h-1.5' : 'bg-border-medium h-1.5'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={() => setCurrent((p) => (p + 1) % testimonials.length)}
              className="w-9 h-9 rounded-full bg-white border border-border-subtle shadow-card flex items-center justify-center hover:bg-surface-2 transition-colors cursor-pointer"
              aria-label="Next"
            >
              <svg className="w-4 h-4 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
